'use client';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import './modal.css';
import classNames from 'classnames';

type Props = {
  children: ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  noBlackout?: boolean;
};

export default function Modal({
  children,
  isOpen,
  onClose,
  noBlackout,
}: Props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!document) return;
    document.body.style.overflow = isOpen ? 'hidden' : 'inherit';
  }, [isOpen]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => e.stopPropagation(),
    []
  );

  if (!mounted) return null;

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={300}
      classNames='modal'
      addEndListener={(node, done) => {
        node.addEventListener('transitionend', done, false);
      }}
      mountOnEnter
      unmountOnExit
    >
      <div
        onClick={onClose}
        className={classNames(
          'fixed bottom-0 left-0 right-0 top-0 z-[1000] flex h-full w-full items-center justify-center',
          !noBlackout && 'bg-bg-overlay'
        )}
      >
        <div
          className='modalBody relative w-full max-w-[600px] overflow-y-auto rounded-2xl px-5'
          onClick={handleClick}
        >
          {children}
        </div>
      </div>
    </CSSTransition>,
    document.getElementById('modal-root')!
  );
}
